import * as ft from 'funtypes'
import { objectWith } from '../validation.js'
import { OperatorSlug } from './operators.js'

/**
 * The eMSP registry: roaming providers, and the operators each one charges at.
 *
 * An eMSP sells the contract, not the hardware. A driver holding one card does
 * not think in operators, they think in "where does this card work", so a
 * provider is a shortcut over the operator facet rather than a facet of its
 * own: picking one names a set of registry slugs, and everything downstream -
 * the map, the URL, the active-filter count - behaves as if those brands had
 * been ticked by hand.
 *
 * Each provider carries its issuer's published coverage list verbatim, one
 * {@link ProviderNetworkEntry} per line, in the issuer's order. Storing the
 * printed name next to the slug it folds onto is what keeps the mapping
 * auditable: the list is the issuer's, the slug is ours, and a test re-derives
 * every one of them through the operator resolver, so a brand renamed in the
 * operator registry fails here rather than quietly dropping a network off a
 * card.
 *
 * A line with no `operator` is one no registry brand answers to - ThreeForce,
 * Last Mile Solution, En+, Columbus Energy, VOL. None of them holds enough
 * Polish sites to be tracked, so their sites sit in the `other` bucket next to
 * several hundred unrelated hotels and car dealers; selecting them would mean
 * selecting all of that too. They are kept on the list and left unmapped rather
 * than approximated, so the count a card shows is the number of brands it can
 * really filter for.
 */

export const ProviderSlug = ft.Union(ft.Literal('evolt'), ft.Literal('eurocharge'))
export type ProviderSlug = ft.Static<typeof ProviderSlug>

export const ProviderNetworkEntry = objectWith(
  {
    /** The network's name as the issuer prints it, spelling and all. */
    name: ft.String,
  },
  {
    /** The registry brand the name folds onto; absent when none tracks it. */
    operator: OperatorSlug,
  },
)
export type ProviderNetworkEntry = ft.Static<typeof ProviderNetworkEntry>

export const RoamingProvider = objectWith(
  {
    slug: ProviderSlug,
    /** The name on the card. A brand, so the same in both languages. */
    displayName: ft.String,
    /**
     * Everything else a driver might type to mean this provider. The search box
     * matches these alongside {@link RoamingProvider.displayName}, so a card
     * whose printed name differs from the brand ("eVolt Charge") is still
     * reachable by the name the driver has in mind.
     */
    aliases: ft.Array(ft.String),
    /** The issuer's published coverage list, in the issuer's order. */
    network: ft.Array(ProviderNetworkEntry),
  },
  {
    website: ft.String,
    /**
     * The issuer's Telegram bot, without the `@`.
     *
     * Both eMSPs here sell the charge through a bot rather than an app, so this
     * is the address a driver standing at the charger actually needs. Absent for
     * an issuer that has none, which is why nothing may assume it is there.
     */
    telegramBot: ft.String,
  },
)
export type RoamingProvider = ft.Static<typeof RoamingProvider>

export const PROVIDERS: readonly RoamingProvider[] = [
  {
    slug: 'evolt',
    displayName: 'eVolt',
    aliases: ['evolt charge', 'e volt'],
    telegramBot: 'evolt_chargebot',
    network: [
      { name: 'ThreeForce' },
      { name: 'Tauron', operator: 'tauron' },
      { name: 'ChargeIn', operator: 'chargein' },
      { name: 'Z75', operator: 'arinea' },
      { name: 'HoryzontEV', operator: 'horyzont-ev' },
      { name: 'BP Pulse', operator: 'bp-pulse' },
      { name: 'Last Mile Solution' },
      { name: 'E.On', operator: 'eon' },
      { name: 'EDRI Poland', operator: 'edri-poland' },
      { name: 'Gwello GmbH', operator: 'qwello' },
      { name: 'Allego', operator: 'allego' },
      { name: 'E-Way24', operator: 'eway' },
      { name: 'En+' },
      { name: 'EVPlus', operator: 'evplus' },
      { name: 'Columbus Energy S.A.' },
      { name: 'ENEFIT', operator: 'enefit' },
      { name: 'VOL' },
    ],
  },
  {
    slug: 'eurocharge',
    displayName: 'EuroCharge',
    aliases: ['euro charge'],
    telegramBot: 'GreenwayChargingbot',
    network: [
      { name: 'Orlen', operator: 'orlen' },
      { name: 'Allego Polen', operator: 'allego' },
      { name: 'Qwello GmbH', operator: 'qwello' },
    ],
  },
]

export const PROVIDERS_BY_SLUG: ReadonlyMap<ProviderSlug, RoamingProvider> = new Map(
  PROVIDERS.map((provider) => [provider.slug, provider]),
)

export const providerBySlug = (slug: ProviderSlug): RoamingProvider => {
  const provider = PROVIDERS_BY_SLUG.get(slug)
  if (provider === undefined) throw new Error(`Unknown provider slug: ${slug}`)
  return provider
}

/**
 * The registry brands a provider charges at, in the issuer's order.
 *
 * Folded once per provider rather than once per render: the filter panel walks
 * this list on every keystroke that redraws it, and the answer never changes.
 */
const OPERATORS_BY_PROVIDER: ReadonlyMap<ProviderSlug, readonly OperatorSlug[]> = new Map(
  PROVIDERS.map((provider) => [
    provider.slug,
    provider.network.flatMap((entry) => (entry.operator === undefined ? [] : [entry.operator])),
  ]),
)

export const providerOperators = (provider: RoamingProvider): readonly OperatorSlug[] =>
  OPERATORS_BY_PROVIDER.get(provider.slug) ?? []

/**
 * The networks on the issuer's list that no tracked brand answers to.
 *
 * Worth surfacing rather than hiding: a card that says "12 operators" when its
 * issuer advertises seventeen is telling the driver something true only if the
 * five missing names can be named.
 */
export const providerUntrackedNetworks = (provider: RoamingProvider): readonly string[] =>
  provider.network.flatMap((entry) => (entry.operator === undefined ? [entry.name] : []))

/** Everything that should match this provider in the search box. */
export const providerSearchText = (provider: RoamingProvider): string =>
  [provider.displayName, ...provider.aliases].join(' ')
